import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import type {Session} from '../types/session'
import {useCurrentRepository} from '@github-ui/current-repository'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {taskByIdApiPath, pullSessionsApiPath} from '../utils/api-url-helpers'
import {maybeNormalizeSession, normalizeHydratedAPITask} from '../types/api-task'
import type {RawHydratedAPITask} from '../types/api-task'
import {useState, useRef, useCallback} from 'react'
import {useParams} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {useDebounce} from '@github-ui/use-debounce'
import {agentTaskQueryKey} from './use-task'
export const CONSECUTIVE_FAILURE_TOLERANCE = 10

export interface UseAgentSessionsProps {
  pullId?: number
  taskId?: string
  sessionsPollingInterval: number
  initialData?: Session[] | undefined
}

export function useSessions({pullId, taskId, initialData, sessionsPollingInterval}: UseAgentSessionsProps) {
  const repo = useCurrentRepository()
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const [timedOut, setHasTimedOut] = useState(false)
  const isRetrying = consecutiveFailures > 0 && !timedOut
  // DEPRECATED: session_id is a built in url param for the session logs page
  const params = useParams<{session_id?: string}>()
  // ?session_id=<session_id> is how we query for session in mission control
  const [searchParams] = useSearchParams()
  const sessionIdInQueryParams = searchParams.get('session_id')
  const sessionIdFromUrl = params.session_id || sessionIdInQueryParams
  const queryByTask = useRef(true)
  const queryClient = useQueryClient()

  const queryIdentifier = taskId ?? pullId

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['agent-sessions', repo.ownerLogin, repo.name, queryIdentifier],
    })
  }, [queryClient, repo.ownerLogin, repo.name, queryIdentifier])

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, sessionsPollingInterval, {maxWait: 10000})

  // Subscribe to alive updates and get connection status for polling fallback
  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
  })

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryIdentifier covers the task/pull; the URL fallback and mutable request mode preserve the existing sessions cache.
  const query = useCAPIQuery<Session[]>({
    queryKey: ['agent-sessions', repo.ownerLogin, repo.name, queryIdentifier],
    queryFn: async () => {
      try {
        let sessions: Session[] = []
        if (!sessionIdFromUrl) {
          queryByTask.current = true
        }

        if (queryByTask.current) {
          let resourcePath: string
          if (taskId) {
            resourcePath = taskByIdApiPath(taskId)
          } else if (pullId !== undefined) {
            resourcePath = pullSessionsApiPath(pullId)
          } else {
            throw new Error('Either taskId or pullId must be provided to fetch sessions')
          }

          const response = await fetchCAPI({
            path: resourcePath,
          })
          const data = await response.json()

          // When fetching from the task endpoint, also update the task query cache
          // so task-level fields (like remote_steerable) stay fresh without a separate poll.
          if (taskId) {
            const raw = (data.task ?? data) as RawHydratedAPITask
            if (raw.id) {
              queryClient.setQueryData(
                agentTaskQueryKey(repo.ownerLogin, repo.name, taskId),
                normalizeHydratedAPITask(raw),
              )
            }
          }

          // Both the task API and legacy pull-sessions endpoints return a sessions array,
          // but the task API uses nested objects (user: {id}) while legacy uses flat IDs (user_id).
          // Normalize to the standard Session shape.
          const rawSessions = data.sessions ?? []
          sessions = rawSessions.map(maybeNormalizeSession)
          const urlSessionInQuery = sessions.some(session => session.id === sessionIdFromUrl)

          if (sessionIdFromUrl && !urlSessionInQuery) {
            queryByTask.current = false
          }
        }

        if (!queryByTask.current && sessionIdFromUrl) {
          const sessionResponse = await fetchCAPI({
            path: `/agents/sessions/${sessionIdFromUrl}`,
          })
          const sessionData = await sessionResponse.json()
          if (sessionData && sessionData.id) {
            sessions = [maybeNormalizeSession(sessionData)]
          }
        }

        // Reset consecutive failures on successful fetch
        setConsecutiveFailures(0)
        setHasTimedOut(false)

        return sessions.sort((a, b) => {
          // Sort sessions by creation date, most recent first
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
      } catch (error) {
        // Increment consecutive failures on error
        setConsecutiveFailures(prev => {
          const newCount = prev + 1
          if (newCount >= CONSECUTIVE_FAILURE_TOLERANCE) {
            setHasTimedOut(true)
          }
          return newCount
        })
        throw error // Re-throw to maintain React Query error state
      }
    },
    initialData: initialData?.map(maybeNormalizeSession),
    // SSR data may have stale task data; treat it as immediately stale to trigger a refetch.
    initialDataUpdatedAt: 0,
    // Use polling as fallback when Alive is not connected (channels failed to load or subscription not established)
    refetchInterval: aliveIsConnected ? false : sessionsPollingInterval,
    // Stop polling after consecutive failure tolerance is reached, or if we have no identifier to query with
    enabled: consecutiveFailures < CONSECUTIVE_FAILURE_TOLERANCE && (pullId !== undefined || !!taskId),
  })

  return {
    ...query,
    timedOut,
    isRetrying,
  }
}
